<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <img src="/assets/img/logos/mattermost.png" alt="Mattermost logo" class="hero-logo">
        <h2>Mattermost</h2>
        <p>A safe messaging and collaboration platform for our students and mentors</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <router-link to="/students" class="back-link">&larr; Back to Student Resources</router-link>

        <h2>What is Mattermost?</h2>
        <p>
          Mattermost is a messaging and collaboration platform — similar to Slack or Discord — that Space Coast STEM
          uses to give students a place to ask questions, share projects, and get help from volunteer mentors.
          It's where the learning continues outside of class: stuck on a coding problem, curious about a project,
          or just want to chat with other students who are into the same things? That's what Mattermost is for.
        </p>

        <h2>Safety First</h2>
        <p>
          The internet isn't always a safe place for students, so we built our Mattermost community with strict
          safeguards. Here's how we keep it safe:
        </p>

        <div class="safety-grid">
          <div class="safety-card">
            <div class="safety-icon">🔒</div>
            <h3>Self-Hosted on Our Server</h3>
            <p>
              Our Mattermost instance runs on hardware we control — not on a public cloud service. We decide who
              has access and how the data is stored.
            </p>
          </div>

          <div class="safety-card">
            <div class="safety-icon">🛡️</div>
            <h3>Background-Checked Users Only</h3>
            <p>
              The only users allowed are students, teachers, and volunteers who have gone through the
              Brevard Public Schools background check process (Raptor).
            </p>
          </div>

          <div class="safety-card">
            <div class="safety-icon">🚫</div>
            <h3>Zero-Tolerance Bullying Policy</h3>
            <p>
              Bullying, harassment, or unkind behavior of any kind results in immediate removal from the platform.
              We want every student to feel welcome and respected.
            </p>
          </div>

          <div class="safety-card">
            <div class="safety-icon">💬</div>
            <h3>No Private Messaging</h3>
            <p>
              Direct/private messages between users are disabled. All conversations happen in public channels where
              they can be seen and moderated, so nothing slips by unseen.
            </p>
          </div>

          <div class="safety-card">
            <div class="safety-icon">👨‍👩‍👧</div>
            <h3>Parent-Friendly Transparency</h3>
            <p>
              Parents are encouraged to monitor their child's use of the platform and can request access to
              review their child's activity at any time. We want families to feel informed and involved.
            </p>
          </div>
        </div>

        <div class="cta-section">
          <h2>Ready to Join?</h2>
          <p>
            To join, you'll need to provide the email of a teacher who can verify you as a current student.
            Once we confirm with them, we'll send you an invite.
          </p>
          <router-link to="/students/mattermost/request" class="btn-cta">
            Request to Join Mattermost &rarr;
          </router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 6rem 2rem;
  text-align: center;
}

.hero-logo {
  width: 100px;
  height: 100px;
  margin-bottom: 1.5rem;
  background: var(--white, #ffffff);
  border-radius: 20px;
  padding: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.hero-content h2 {
  font-size: 3rem;
  margin: 0 0 1rem 0;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
  max-width: 700px;
  margin: 0 auto;
}

.content-section {
  padding: 4rem 2rem;
}

.back-link {
  display: inline-block;
  color: var(--secondary-color, #3ba3d1);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
}

.back-link:hover {
  text-decoration: underline;
}

.content-section h2 {
  font-size: 2.2rem;
  margin: 2rem 0 1rem 0;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section h2:first-of-type {
  margin-top: 0;
}

.content-section p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-light, #444444);
  margin-bottom: 1rem;
}

.safety-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0 3rem 0;
}

.safety-card {
  background: var(--white, #ffffff);
  padding: 1.75rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  border-left: 4px solid var(--secondary-color, #3ba3d1);
}

.safety-icon {
  font-size: 2.2rem;
  margin-bottom: 0.75rem;
}

.safety-card h3 {
  font-family: 'Poppins', sans-serif;
  color: var(--primary-color, #2c4a6b);
  font-size: 1.15rem;
  margin: 0 0 0.6rem 0;
}

.safety-card p {
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--text-light, #555555);
}

.cta-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #e6eef5 100%);
  padding: 2.5rem;
  border-radius: 12px;
  text-align: center;
  margin-top: 3rem;
}

.cta-section h2 {
  margin-top: 0;
}

.cta-section p {
  max-width: 600px;
  margin: 0 auto 1.5rem auto;
}

.btn-cta {
  display: inline-block;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 1rem 2.5rem;
  border-radius: 30px;
  text-decoration: none;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  transition: transform 0.3s, box-shadow 0.3s;
}

.btn-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(59, 163, 209, 0.4);
}

@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero {
    padding: 4rem 1.5rem;
  }

  .content-section h2 {
    font-size: 1.75rem;
  }

  .cta-section {
    padding: 2rem 1rem;
  }
}
</style>
